import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Title as TitleIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

function ServicioForm() {
  const [servicio, setServicio] = useState({
    nombre_servicio: '',
    descripcion_servicio: '',
    precio: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isEditing = Boolean(id);

  useEffect(() => {
    if (id) {
      fetchServicio(id);
    }
  }, [id]);

  const fetchServicio = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/api/servicios/${id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setServicio(response.data);
    } catch (error) {
      console.error('Error al obtener el servicio:', error);
      setError('Error al cargar los datos del servicio');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!servicio.nombre_servicio.trim()) {
      errors.nombre_servicio = 'El nombre del servicio es requerido';
    } else if (servicio.nombre_servicio.length < 3) {
      errors.nombre_servicio = 'El nombre debe tener al menos 3 caracteres';
    }
    
    if (!servicio.precio || servicio.precio <= 0) {
      errors.precio = 'El precio debe ser mayor a 0';
    } else if (isNaN(servicio.precio)) {
      errors.precio = 'El precio debe ser un número válido';
    }
    
    if (servicio.descripcion_servicio && servicio.descripcion_servicio.length > 500) {
      errors.descripcion_servicio = 'La descripción no puede exceder 500 caracteres';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setServicio(prev => ({ ...prev, [name]: value }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      if (id) {
        await axios.put(`${config.API_BASE_URL}/api/servicios/${id}`, servicio, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      } else {
        await axios.post(`${config.API_BASE_URL}/api/servicios`, servicio, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/home/servicios');
      }, 1500);
      
    } catch (error) {
      console.error('Error al guardar el servicio:', error);
      setError(isEditing ? 'Error al actualizar el servicio' : 'Error al crear el servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/home/servicios');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const getServiceIcon = () => {
    const nombre = servicio.nombre_servicio?.toLowerCase() || '';
    if (nombre.includes('limpieza')) return '🧹';
    if (nombre.includes('mantenimiento')) return '🔧';
    if (nombre.includes('seguridad')) return '🛡️';
    if (nombre.includes('jardinería')) return '🌿';
    if (nombre.includes('cocina')) return '👨‍🍳';
    return '⚙️';
  };

  if (loading && isEditing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#1A237E' }} />
        <Typography variant="h6" color="text.secondary">
          Cargando datos del servicio...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header con botón volver */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{
              color: '#1A237E',
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Volver
          </Button>
          
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              {isEditing ? 'Editar Servicio' : 'Crear Servicio'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? 'Modifica la información del servicio' : 'Ingresa los datos del nuevo servicio'}
            </Typography>
          </Box>
        </Box>

        {/* Preview del icono del servicio */}
        {servicio.nombre_servicio && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(79, 195, 247, 0.1)',
            border: '1px solid rgba(79, 195, 247, 0.3)'
          }}>
            <Box sx={{ fontSize: '2rem' }}>
              {getServiceIcon()}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Vista previa
            </Typography>
          </Box>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Servicio {isEditing ? 'actualizado' : 'creado'} exitosamente. Redirigiendo...
        </Alert>
      )}

      {/* Formulario */}
      <Paper sx={{ 
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid rgba(26, 35, 126, 0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
      }}>
        {/* Header del formulario */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: 'white',
          p: { xs: 3, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <WorkIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold' }}>
              Información del Servicio
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Define los detalles de tu servicio
            </Typography>
          </Box>
        </Box>

        {/* Contenido del formulario */}
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              
              {/* Nombre del servicio */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2 }}>
                  <TitleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Información Básica
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre del Servicio"
                  name="nombre_servicio"
                  value={servicio.nombre_servicio}
                  onChange={handleChange}
                  required
                  error={!!fieldErrors.nombre_servicio}
                  helperText={fieldErrors.nombre_servicio}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon sx={{ color: '#4FC3F7' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                  placeholder="Ej: Limpieza General, Mantenimiento Preventivo..."
                />
              </Grid>

              {/* Precio */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Precio del Servicio"
                  name="precio"
                  type="number"
                  value={servicio.precio}
                  onChange={handleChange}
                  required
                  error={!!fieldErrors.precio}
                  helperText={fieldErrors.precio || `Precio en moneda local (${simboloMoneda})`}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon sx={{ color: '#4CAF50' }} />
                      </InputAdornment>
                    ),
                    inputProps: { 
                      min: 0, 
                      step: 0.01,
                      placeholder: "0.00"
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              {/* Precio formateado preview */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid rgba(76, 175, 80, 0.3)'
                }}>
                  <MoneyIcon sx={{ color: '#4CAF50', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    {simboloMoneda} {servicio.precio ? parseFloat(servicio.precio).toFixed(2) : '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Precio final
                  </Typography>
                </Box>
              </Grid>

              {/* Descripción */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2, mt: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Descripción Detallada
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción del Servicio"
                  name="descripcion_servicio"
                  value={servicio.descripcion_servicio}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  error={!!fieldErrors.descripcion_servicio}
                  helperText={
                    fieldErrors.descripcion_servicio || 
                    `${servicio.descripcion_servicio.length}/500 caracteres`
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                  placeholder="Describe en detalle qué incluye este servicio, métodos utilizados, tiempo estimado, etc."
                />
              </Grid>

              {/* Botones de acción */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2, 
                  justifyContent: 'flex-end',
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid rgba(26, 35, 126, 0.1)'
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    sx={{
                      borderColor: '#1A237E',
                      color: '#1A237E',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        borderColor: '#0D47A1',
                        backgroundColor: 'rgba(26, 35, 126, 0.04)'
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                        boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: '#E0E0E0',
                        color: '#999'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? 'Guardando...' : (isEditing ? 'Actualizar Servicio' : 'Crear Servicio')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>

      {/* Tips y recomendaciones */}
      <Paper sx={{ 
        mt: 3,
        p: 3,
        borderRadius: 3,
        border: '1px solid rgba(33, 150, 243, 0.3)',
        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)'
      }}>
        <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold', mb: 2 }}>
          💡 Consejos para crear un buen servicio
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>• Nombre claro:</strong> Usa nombres descriptivos y fáciles de entender
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>• Precio competitivo:</strong> Investiga precios del mercado
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>• Descripción detallada:</strong> Incluye qué está incluido y excluido
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>• Tiempo estimado:</strong> Menciona la duración aproximada
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default ServicioForm;